<template>
  <v-form
    ref="form"
    class="custom-setting-wrap">
    <!-- Toolbar -->
    <div class="toolbar-wrap">
      <div class="toolbar-hint">
        <v-icon
          small
          color="grey lighten-1"
          class="mr-1">
          mdi-drag
        </v-icon>
        <span class="caption grey--text">ลากเพื่อจัดลำดับ Priority</span>
      </div>
      <div>
        <v-btn
          color="primary"
          outlined
          depressed
          class="mr-2"
          :loading="loading"
          @click="newRule()">
          <v-icon
            left
            small>
            mdi-plus
          </v-icon>
          New Rule
        </v-btn>
        <v-btn
          color="primary"
          depressed
          outlined
          :loading="loading"
          :disabled="disableSave"
          @click="savePrioritySetting()">
          <v-icon
            left
            small>
            mdi-content-save
          </v-icon>
          Save
        </v-btn>
      </div>
    </div>

    <!-- Tag List Header -->
    <div class="setting-table-header">
      <div class="col-drag" />
      <div class="col-expand" />
      <div
        v-for="(header, index) of settingHeaders"
        :key="'header-' + index"
        :class="['setting-col', `setting-col-${header.value}`, `text-${header.align || 'left'}`]">
        {{ header.text }}
      </div>
    </div>

    <!-- Tag Rows (collapsible list) -->
    <draggable
      :list="settings"
      group="setting"
      handle=".drag-handle"
      tag="div">
      <div
        v-for="(item, index) of settings"
        :key="'item-' + index"
        class="setting-row-group"
        :class="{ 'is-expanded': expandedIndex === index }">
        <!-- Tag Row -->
        <div
          class="setting-row"
          @click="toggleExpand(index)">
          <div class="col-drag">
            <v-icon
              class="drag-handle"
              small
              color="grey lighten-1"
              @click.stop>
              mdi-drag
            </v-icon>
          </div>
          <div class="col-expand">
            <v-icon
              small
              :class="{ 'expand-icon-rotated': expandedIndex === index }"
              class="expand-icon">
              mdi-chevron-right
            </v-icon>
          </div>
          <div
            class="setting-col setting-col-tag">
            <v-chip
              small
              label
              color="blue-grey lighten-5">
              {{ item.tag }}
            </v-chip>
          </div>
          <div
            class="setting-col setting-col-channels">
            <v-chip
              v-for="ch in activeChannelsOf(item)"
              :key="ch"
              x-small
              class="mr-1"
              color="teal lighten-5"
              text-color="teal darken-2">
              {{ ch }}
            </v-chip>
          </div>
          <div
            class="setting-col setting-col-actions text-right">
            <v-btn
              icon
              x-small
              class="action-btn mr-1"
              @click.stop="editSetting(item)">
              <v-icon small>
                mdi-pencil
              </v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              class="action-btn action-btn-delete"
              @click.stop="confirmRemove(item)">
              <v-icon small>
                mdi-delete-outline
              </v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Expanded Warehouse Detail (toggle inside each channel cell) -->
        <div
          class="warehouse-detail"
          :class="{ 'warehouse-detail-open': expandedIndex === index }">
          <div class="warehouse-detail-inner">
            <table class="warehouse-table">
              <thead>
                <tr>
                  <th width="40"></th>
                  <th
                    v-for="(head, WHDIndex) of warehouseHeaders"
                    :key="`${WHDIndex}-${index}-warehouseHeader`"
                    :class="`text-${head.align || 'left'}`">
                    {{ head.text }}
                  </th>
                </tr>
              </thead>
              <draggable
                :list="item.warehouses"
                :group="`warehouses-${index}`"
                handle=".wh-drag-handle"
                tag="tbody">
                <tr
                  v-for="(wh, whIndex) in item.warehouses"
                  :key="`warehouse-index-${index}-${whIndex}`"
                  class="wh-row"
                  :class="{ 'wh-row-editing': editWarehouseKey === `${index}-${whIndex}` }">
                  <td class="wh-drag-cell">
                    <v-icon
                      class="wh-drag-handle"
                      x-small
                      color="grey lighten-1">
                      mdi-drag
                    </v-icon>
                  </td>
                  <td>
                    <span class="caption grey--text">#{{ wh.id }}</span>
                  </td>
                  <td>
                    <span class="font-weight-medium">{{ wh.name }}</span>
                  </td>
                  <td
                    v-for="ch in channels"
                    :key="`ch-${ch.value}-${index}-${whIndex}`"
                    class="text-center channel-cell">
                    <div class="channel-cell-inner">
                      <v-switch
                        v-model="settings[index].warehouses[whIndex].channels[ch.value].status"
                        dense
                        hide-details
                        class="mt-0 pt-0 channel-switch"
                        true-value="active"
                        false-value="inactive" />
                      <v-text-field
                        v-if="editWarehouseKey === `${index}-${whIndex}`"
                        v-model="settings[index].warehouses[whIndex].channels[ch.value].minStock"
                        :rules="numberFieldRules"
                        hide-details
                        :error="numberFieldRules.some((r) => r(settings[index].warehouses[whIndex].channels[ch.value].minStock) !== true)"
                        class="text-center inline-edit-field"
                        outlined
                        dense
                        :readonly="loading"
                        placeholder="0" />
                      <span
                        v-else
                        class="stock-value"
                        :class="{ 'stock-inactive': wh.channels[ch.value].status !== 'active' }">
                        {{ wh.channels[ch.value].minStock }}
                      </span>
                    </div>
                  </td>
                  <td class="text-center">
                    <v-simple-checkbox
                      v-model="settings[index].warehouses[whIndex].priorityWarehouse"
                      :ripple="false"
                      color="primary" />
                  </td>
                  <td class="text-center">
                    <v-btn
                      v-if="editWarehouseKey === `${index}-${whIndex}`"
                      icon
                      x-small
                      color="success"
                      class="mr-1"
                      :disabled="!isEditRowValid"
                      @click="editWarehouseKey = null">
                      <v-icon small>
                        mdi-check
                      </v-icon>
                    </v-btn>
                    <v-btn
                      v-if="editWarehouseKey === `${index}-${whIndex}`"
                      icon
                      x-small
                      color="grey"
                      @click="editWarehouseKey = null">
                      <v-icon small>
                        mdi-close
                      </v-icon>
                    </v-btn>
                    <v-btn
                      v-else
                      icon
                      x-small
                      class="wh-edit-btn"
                      @click="editWarehouseKey = `${index}-${whIndex}`">
                      <v-icon small>
                        mdi-pencil
                      </v-icon>
                    </v-btn>
                  </td>
                </tr>
              </draggable>
            </table>
          </div>
        </div>
      </div>
    </draggable>

    <!-- Empty State -->
    <div
      v-if="!loading && settings.length === 0"
      class="empty-state">
      <v-icon
        large
        color="grey lighten-2"
        class="mb-2">
        mdi-inbox-outline
      </v-icon>
      <div class="caption grey--text">
        ยังไม่มี Custom Rule
      </div>
    </div>

    <!-- Dialog -->
    <v-dialog
      v-model="dialog"
      max-width="480px">
      <v-card class="pa-2">
        <form @submit.prevent="saveModalAction()">
          <v-card-title class="text-h6 pb-2">
            {{ editItem ? 'Edit Rule' : 'New Rule' }}
          </v-card-title>
          <v-card-text class="pb-0">
            <v-row v-if="dialog">
              <v-col :cols="12">
                <v-autocomplete
                  v-model="formData.tag"
                  :items="tags"
                  outlined
                  dense
                  label="Tag"
                  placeholder="เลือก Tag"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="px-6 pb-4">
            <v-spacer />
            <v-btn
              text
              :loading="loading"
              @click="closeFormModal()">
              Cancel
            </v-btn>
            <v-btn
              :loading="loading"
              color="primary"
              :disabled="formData.tag == '' || formData.tag == null"
              outlined
              type="submit">
              {{ editItem ? 'Update' : 'Create' }}
            </v-btn>
          </v-card-actions>
        </form>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import WarehouseProvider from '@/resources/WarehouseProvider'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import OrderfulfillmentSettingNewProvider from '@/resources/OrderfulfillmentSettingNewProvider'
import Draggable from 'vuedraggable'

const WarehouseService = new WarehouseProvider()
const ProductAttributeService = new ProductAttributeProvider()
const SettingService = new OrderfulfillmentSettingNewProvider()

const CHANNELS = [
  { text: 'Web', value: 'web' },
  { text: 'Tiktok', value: 'tiktok' },
  { text: 'Lazada', value: 'lazada' }
]

const settingHeaders = [
  { text: 'Tag', value: 'tag', flex: 2, sortable: false },
  { text: 'Channels', value: 'channels', flex: 2, sortable: false },
  { text: 'Actions', value: 'actions', sortable: false, flex: 1, align: 'right' }
]

const warehouseHeaders = [
  { text: 'ID', value: 'id' },
  { text: 'Name', value: 'name' },
  ...CHANNELS.map((c) => ({ text: c.text, value: c.value, align: 'center' })),
  { text: 'Priority WH', value: 'priorityWarehouse', align: 'center' },
  { text: 'Actions', value: 'actions', align: 'center' }
]

const emptyChannelMap = () => CHANNELS.reduce((acc, ch) => {
  acc[ch.value] = { status: 'inactive', minStock: '0' }
  return acc
}, {})

const buildEmptyWarehouseRows = (warehouses) => warehouses.map((w) => ({
  id: w.id,
  name: w.name,
  priorityWarehouse: false,
  channels: emptyChannelMap()
}))

export default {
  components: {
    Draggable
  },
  data () {
    return {
      loading: false,
      channels: CHANNELS,
      tags: [],
      warehouses: [],
      editItem: null,
      expandedIndex: null,
      dialog: false,
      formData: {
        tag: ''
      },
      warehouseHeaders,
      settingHeaders,
      editWarehouseKey: null,
      memoSettings: [],
      settings: [],
      numberFieldRules: [
        // eslint-disable-next-line no-mixed-operators
        (v) => v !== '' && v !== null && v !== undefined || 'Required.',
        (v) => /^\d+$/.test(v) || 'Invalid.'
      ]
    }
  },
  computed: {
    disableSave () {
      return JSON.stringify(this.memoSettings) === JSON.stringify(this.settings) || this.loading
    },
    isEditRowValid () {
      if (!this.editWarehouseKey) return true
      const [sIdx, wIdx] = this.editWarehouseKey.split('-').map(Number)
      const wh = this.settings[sIdx]?.warehouses[wIdx]
      if (!wh) return true
      return this.channels.every((ch) => {
        const val = wh.channels[ch.value].minStock
        return this.numberFieldRules.every((rule) => rule(val) === true)
      })
    }
  },
  async created () {
    await this.getWarehouse()
    this.getTags()
    this.fetchData()
  },
  methods: {
    newRule () {
      this.editItem = null
      this.formData = { tag: '' }
      this.dialog = true
    },
    activeChannelsOf (rule) {
      const set = new Set()
      rule.warehouses.forEach((w) => {
        Object.keys(w.channels).forEach((ch) => {
          if (w.channels[ch].status === 'active') set.add(ch)
        })
      })
      return CHANNELS.map((c) => c.value).filter((c) => set.has(c))
    },
    closeFormModal () {
      this.editItem = null
      this.dialog = false
    },
    toggleExpand (index) {
      this.expandedIndex = this.expandedIndex === index ? null : index
      this.editWarehouseKey = null
    },
    editSetting (item) {
      this.editItem = item
      this.formData = { tag: item.tag }
      this.dialog = true
    },
    async confirmRemove (item) {
      this.$store.dispatch('Components/setModal', {
        value: true,
        title: 'Remove Setting',
        message: 'Do you want to remove this Setting',
        confirmText: 'Remove',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.remove(item.tag)
      })
    },
    async remove (tag) {
      try {
        this.loading = true
        await SettingService.deleteByTag(tag)
        this.fetchData()
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', { value: true, message: error.message || 'Something went wrong.', type: 'error' })
      } finally {
        this.loading = false
      }
    },
    /**
     * โหลด custom rule จาก backend (per channel docs) แล้ว group by tag
     */
    async fetchData () {
      try {
        this.loading = true
        const { data: docs } = await SettingService.getMany({ is_default: 0 })

        // group docs by tag
        const groupByTag = new Map()
        docs.forEach((d) => {
          if (!groupByTag.has(d.tag)) {
            groupByTag.set(d.tag, {
              tag: d.tag,
              priority: d.priority,
              warehousesByChannel: {}
            })
          }
          groupByTag.get(d.tag).warehousesByChannel[d.channel] = d.warehouses
        })

        // for each tag, build warehouses array merged across channels
        this.settings = [...groupByTag.values()]
          .map((rule) => ({
            tag: rule.tag,
            priority: rule.priority,
            warehouses: this.mergeWarehousesAcrossChannels(rule.warehousesByChannel)
          }))
          .sort((a, b) => a.priority - b.priority)

        this.memoSettings = JSON.parse(JSON.stringify(this.settings))
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', { value: true, message: error.message || 'Something went wrong.', type: 'error' })
      } finally {
        this.loading = false
      }
    },
    mergeWarehousesAcrossChannels (warehousesByChannel) {
      // priority/order สำหรับ warehouse ใช้จาก channel ใดก็ได้ (sync เท่ากัน)
      const referenceList = warehousesByChannel[CHANNELS[0].value]
        || Object.values(warehousesByChannel)[0]
        || []

      const orderIds = referenceList.map((w) => w.id)
      const allWarehouses = this.warehouses.length
        ? this.warehouses
        : referenceList

      const merged = []
      const seen = new Set()

      // ใส่ warehouse ตามลำดับใน reference ก่อน
      orderIds.forEach((id) => {
        const meta = allWarehouses.find((w) => w.id === id) || referenceList.find((w) => w.id === id)
        if (!meta) return
        seen.add(id)
        const refWh = referenceList.find((w) => w.id === id)
        const row = {
          id: meta.id,
          name: meta.name,
          priorityWarehouse: refWh?.priorityWarehouse || false,
          channels: emptyChannelMap()
        }
        CHANNELS.forEach((ch) => {
          const list = warehousesByChannel[ch.value] || []
          const found = list.find((w) => w.id === id)
          if (found) {
            row.channels[ch.value] = {
              status: found.status || 'inactive',
              minStock: String(found.minStock || 0)
            }
          }
        })
        merged.push(row)
      })

      // warehouse ใหม่ที่ master มี แต่ rule ยังไม่มี → ใส่ท้าย เป็น inactive
      allWarehouses.forEach((w) => {
        if (seen.has(w.id)) return
        merged.push({
          id: w.id,
          name: w.name,
          priorityWarehouse: false,
          channels: emptyChannelMap()
        })
      })

      return merged
    },
    /**
     * Save ทั้งหมด — frontend ส่ง 1 payload, backend วน 4 channels
     */
    async savePrioritySetting () {
      if (!this.$refs.form.validate()) return
      try {
        this.loading = true
        const payload = this.settings.map((rule, idx) => ({
          tag: rule.tag,
          priority: idx + 1,
          warehouses: rule.warehouses.map((w, i) => ({
            id: w.id,
            name: w.name,
            priority: i,
            priorityWarehouse: w.priorityWarehouse || false,
            channels: CHANNELS.map((ch) => ({
              channel: ch.value,
              status: w.channels[ch.value].status,
              minStock: Number(w.channels[ch.value].minStock || 0)
            }))
          }))
        }))

        await SettingService.saveCustomGrouped({ rules: payload })
        await this.fetchData()
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', { value: true, message: error.message || 'Something went wrong.', type: 'error' })
      } finally {
        this.loading = false
      }
    },
    async saveModalAction () {
      try {
        this.loading = true
        if (this.editItem === null) {
          // create rule ใหม่ — backend จะ create 4 docs (1 per channel)
          await SettingService.createTagRule({
            tag: this.formData.tag,
            warehouses: buildEmptyWarehouseRows(this.warehouses).map((w, i) => ({
              id: w.id,
              name: w.name,
              priority: i,
              priorityWarehouse: false,
              channels: CHANNELS.map((ch) => ({
                channel: ch.value,
                status: 'inactive',
                minStock: 0
              }))
            }))
          })
        } else {
          const priorityPayload = this.settings.map((rule, idx) => ({
            tag: rule.tag,
            priority: idx + 1,
            warehouses: rule.warehouses.map((w, i) => ({
              id: w.id,
              name: w.name,
              priority: i,
              priorityWarehouse: w.priorityWarehouse || false,
              channels: CHANNELS.map((ch) => ({
                channel: ch.value,
                status: w.channels[ch.value].status,
                minStock: Number(w.channels[ch.value].minStock || 0)
              }))
            }))
          }))
          await SettingService.saveCustomGrouped({ rules: priorityPayload })
          await SettingService.renameTag(this.editItem.tag, this.formData.tag)
        }
        this.dialog = false
        this.editItem = null
        this.formData = { tag: '' }
        this.fetchData()
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', { value: true, message: error.message || 'Something went wrong.', type: 'error' })
      } finally {
        this.loading = false
      }
    },
    async getTags () {
      try {
        const { data } = await ProductAttributeService.getProductAttribute('tags', {
          page: 1,
          itemsPerPage: 9999999,
          sortBy: ['id'],
          sortDesc: [true]
        })
        this.tags = data.results.map((r) => r.name)
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', { value: true, message: error.message || 'Something went wrong.', type: 'error' })
      }
    },
    async getWarehouse () {
      try {
        const { data } = await WarehouseService.getWarehouse()
        this.warehouses = data.filter((w) => w.status === 'active')
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', { value: true, message: error.message || 'Something went wrong.', type: 'error' })
      }
    }
  }
}
</script>

<style scoped>
.custom-setting-wrap {
  background: #fff;
}
.toolbar-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px;
}
.toolbar-hint {
  display: flex;
  align-items: center;
}

/* Setting Table Header */
.setting-table-header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.col-drag {
  width: 32px;
  flex-shrink: 0;
}
.col-expand {
  width: 32px;
  flex-shrink: 0;
}
.setting-col {
  padding: 0 8px;
}
.setting-col-tag {
  flex: 2;
}
.setting-col-channels {
  flex: 2;
}
.setting-col-actions {
  flex: 1;
}

/* Tag Rows */
.setting-row-group {
  border-bottom: 1px solid #f0f0f0;
}
.setting-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s;
}
.setting-row:hover {
  background-color: #f5f7fa;
}
.drag-handle {
  cursor: grab;
  opacity: 0.3;
  transition: opacity 0.15s;
}
.setting-row:hover .drag-handle {
  opacity: 1;
}
.expand-icon {
  transition: transform 0.2s ease;
  color: #999;
}
.expand-icon-rotated {
  transform: rotate(90deg);
}
.action-btn {
  opacity: 0.4;
  transition: opacity 0.15s;
}
.setting-row:hover .action-btn {
  opacity: 1;
}
.action-btn-delete:hover {
  color: #e53935 !important;
}

/* Warehouse Expanded Detail */
.warehouse-detail {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.35s ease;
  background: #fafbfc;
}
.warehouse-detail-open {
  max-height: calc(-400px + 100vh);
  overflow: auto;
}
.warehouse-detail-inner {
  padding: 8px 24px 16px 64px;
}
.warehouse-table {
  border-collapse: collapse;
  width: 100%;
}
.warehouse-table th {
  padding: 8px 6px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 1px solid #e8e8e8;
}
.warehouse-table td {
  padding: 6px;
  border: none;
  border-bottom: 1px solid #f0f0f0;
}
.wh-row {
  transition: background-color 0.15s;
}
.wh-row:hover {
  background-color: #f0f4f8 !important;
}
.wh-row-editing {
  background-color: #e8f5e9 !important;
}
.wh-drag-handle {
  cursor: grab;
  opacity: 0.3;
  transition: opacity 0.15s;
}
.wh-row:hover .wh-drag-handle {
  opacity: 1;
}
.wh-drag-cell {
  width: 32px;
  padding: 0 4px !important;
}
.wh-edit-btn {
  opacity: 0.3;
  transition: opacity 0.15s;
}
.wh-row:hover .wh-edit-btn {
  opacity: 1;
}
.channel-cell {
  vertical-align: middle;
}
.channel-cell-inner {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
}
.stock-value {
  font-variant-numeric: tabular-nums;
  color: #555;
  min-width: 24px;
  text-align: center;
}
.stock-value.stock-inactive {
  opacity: 0.4;
}
.inline-edit-field {
  max-width: 70px;
  margin: 0 auto;
}
.text-center >>> input {
  text-align: center;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;
}
</style>
